import React from 'react';
import { ItarScreen, itarScreenViewOf } from 'components/ExportDesign/exportDesignResultCardData';

// --- The early ITAR answer (UX slice S5, board 03): the result card in its ITAR-only state, shown under
// the live run panel as soon as the classifier records its US Munitions List conclusion. Preliminary: the
// EAR lane is still working (a skeleton) and the turn's final answer replaces it with the full card. Only
// <div>s inside: the chat bubble's `p` margin rule outranks single classes.

interface ExportDesignItarScreenCardProps {
  screen: ItarScreen;
}

const ExportDesignItarScreenCard: React.FC<ExportDesignItarScreenCardProps> = ({ screen }) => {
  const view = itarScreenViewOf(screen);
  const pending = !view.onUsml;

  return (
    <div className='export-design-result export-design-result-early'>
      <article className='export-design-block' aria-label='Classification, in progress' aria-live='polite'>
        <div className='export-design-block-h'>
          <div>
            <div className='export-design-block-kicker'>Classification, in progress</div>
            <h3 className='export-design-block-title'>{view.title}</h3>
            <div className='export-design-block-sub'>{view.sub}</div>
          </div>
          <div className='export-design-block-pills'>
            <span className='export-design-pill is-neutral'>Preliminary</span>
          </div>
        </div>

        <div className='export-design-track' aria-label='Where this product sits so far'>
          <div
            className={`export-design-track-seg${view.onUsml ? ' is-here regime-itar' : ' is-cleared'}`}
            aria-current={view.onUsml ? 'true' : undefined}
          >
            <div className='t'>ITAR (USML)</div>
            <div className='v'>{view.itarValue}</div>
          </div>
          <div className={`export-design-track-seg${pending ? ' is-checking' : ''}`}>
            <div className='t'>EAR-listed (CCL)</div>
            <div className='v'>{pending ? 'Checking…' : "Doesn't apply"}</div>
          </div>
          <div className={`export-design-track-seg${pending ? ' is-checking' : ''}`}>
            <div className='t'>EAR99</div>
            <div className='v'>{pending ? 'Checking…' : 'Not reached'}</div>
          </div>
        </div>

        {(screen.reasons.length > 0 || screen.closest_checked.length > 0 || screen.open_question) && (
          <section>
            <div className='export-design-section-h'>{view.lane}</div>
            <div className='export-design-inset export-design-inset-text export-design-screen-reasons'>
              {screen.reasons.map((reason) => (
                <div key={reason}>{reason}</div>
              ))}
              {screen.closest_checked.length > 0 && (
                <div className='export-design-screen-muted'>Checked: {screen.closest_checked.join(', ')}.</div>
              )}
              {screen.open_question && <div className='export-design-screen-muted'>{screen.open_question}</div>}
            </div>
          </section>
        )}

        {pending ? (
          <section>
            <div className='export-design-section-h'>EAR check: Commerce Control List</div>
            <div className='export-design-inset export-design-skeleton' aria-label='Still checking'>
              <div className='export-design-skel' style={{ width: '46%' }} />
              <div className='export-design-skel' style={{ width: '78%' }} />
              <div className='export-design-skel' style={{ width: '64%' }} />
            </div>
          </section>
        ) : (
          <section>
            <div className='export-design-section-h'>EAR check: not needed</div>
            <div className='export-design-inset export-design-inset-text'>
              An item on the USML stays under ITAR, so the Commerce Control List doesn&apos;t apply to it.
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default ExportDesignItarScreenCard;
